/**
 * Admin Shop Tab - Manage products
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { ShoppingBag, Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminShopTab = () => {
  const { products, loadData } = useAdmin();
  
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "", description: "", price: "", category: "accessories", stock: "10", image_url: "", is_active: true
  });

  const resetProductForm = () => {
    setProductForm({ name: "", description: "", price: "", category: "accessories", stock: "10", image_url: "", is_active: true });
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error("Name and price required");
      return;
    }
    try {
      const data = { ...productForm, price: parseFloat(productForm.price), stock: parseInt(productForm.stock) };
      if (editingProduct) {
        await axios.put(`${API}/shop/products/${editingProduct.product_id}`, data, { withCredentials: true });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/shop/products`, data, { withCredentials: true });
        toast.success("Product added");
      }
      resetProductForm();
      loadData();
    } catch { toast.error("Failed to save product"); }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || "",
      description: product.description || "",
      price: product.price?.toString() || "",
      category: product.category || "accessories",
      stock: product.stock?.toString() || "10",
      image_url: product.image_url || "",
      is_active: product.is_active !== false
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/shop/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted");
      loadData();
    } catch { toast.error("Delete failed"); }
  };

  const handleToggleActive = async (product) => {
    try {
      await axios.put(`${API}/shop/products/${product.product_id}`, { is_active: !product.is_active }, { withCredentials: true });
      toast.success(product.is_active ? "Product hidden" : "Product visible");
      loadData();
    } catch { toast.error("Update failed"); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle><ShoppingBag className="w-5 h-5 inline mr-2" />Shop Products</CardTitle>
          <CardDescription>Manage shop products and inventory</CardDescription>
        </div>
        <Button onClick={() => { resetProductForm(); setShowProductForm(true); }} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </CardHeader>
      <CardContent>
        {showProductForm && (
          <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h3 className="font-semibold mb-4">{editingProduct ? "Edit Product" : "Add Product"}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} /></div>
              <div><Label>Price (£) *</Label><Input type="number" step="0.01" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} /></div>
              <div><Label>Category</Label>
                <Select value={productForm.category} onValueChange={v => setProductForm({...productForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jewelry">Jewelry</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="clothing">Clothing</SelectItem>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="toys">Toys</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Stock</Label><Input type="number" value={productForm.stock} onChange={e => setProductForm({...productForm, stock: e.target.value})} /></div>
              <div className="md:col-span-2"><Label>Image URL</Label><Input value={productForm.image_url} onChange={e => setProductForm({...productForm, image_url: e.target.value})} placeholder="https://..." /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSaveProduct} className="bg-amber-600 hover:bg-amber-700">{editingProduct ? "Update" : "Add"} Product</Button>
              <Button variant="outline" onClick={resetProductForm}>Cancel</Button>
            </div>
          </div>
        )}
        
        {products.length === 0 ? (
          <p className="text-center text-stone-500 py-8">No products yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map(product => (
                <TableRow key={product.product_id}>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell className="capitalize">{product.category}</TableCell>
                  <TableCell>£{product.price?.toFixed(2)}</TableCell>
                  <TableCell>{product.stock}</TableCell>
                  <TableCell>
                    <Badge variant={product.is_active !== false ? "default" : "secondary"}>
                      {product.is_active !== false ? "Active" : "Hidden"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleToggleActive(product)}>
                        {product.is_active !== false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEditProduct(product)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteProduct(product.product_id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminShopTab;
